import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Wait for every animated property, not elapsed wall time. Cancellation also invalidates callbacks
 * already queued from the UI thread. If the animation runtime stops reporting, snap to the final
 * visual state before releasing the gate. The snap acknowledgement has its own deadline; failure
 * replaces the animation session with static recovery rather than revealing unconfirmed geometry.
 */
export function onboardingTransitionCompletion({
  onFailed,
  onReady,
  parts,
  reduceMotion,
  snapToEnd,
}: {
  /** Unmount the failed animation session and offer a visible retry; never reveal gated content. */
  onFailed: () => void;
  onReady: () => void;
  parts: number;
  reduceMotion: boolean;
  snapToEnd: (onSnapped: () => void) => void;
}) {
  let active = true;
  let isSnapping = false;
  const completed = new Set<number>();
  let settle = () => {};
  const cancel = () => {
    active = false;
    settle();
  };
  const completePart = (part: number, finished: boolean) => {
    if (!active || isSnapping) {
      return;
    }
    if (!finished) {
      cancel();
      return;
    }
    completed.add(part);
    if (completed.size === parts) {
      settle();
    }
  };
  const ready = () => {
    if (active) {
      active = false;
      onReady();
    }
  };
  const snap = () => {
    isSnapping = true;
    void boundedPromise<void>(
      { deadline: 1500, operation: "Onboarding snap acknowledgement" },
      (resolve) => {
        settle = resolve;
        snapToEnd(() => {
          resolve();
          ready();
        });
      },
    ).catch(() => {
      if (active) {
        active = false;
        onFailed();
      }
    });
  };
  if (reduceMotion) {
    snap();
  } else {
    void boundedPromise(
      { deadline: 3000, onDeadline: "resolve", operation: "Onboarding transition completion" },
      (resolve) => {
        settle = resolve;
      },
    ).then(() => {
      if (!active) {
        return;
      }
      if (completed.size !== parts) {
        snap();
      } else {
        ready();
      }
    });
  }
  return { cancel, completePart };
}
